import { Media } from '@digilize/shared/definitions/src';

import { addWebp } from './addWebp';

export type ResizeFit = 'cover' | 'contain';

/* The endpoint's mode parameter. A mode sent with only one dimension silently returns the original,
   so a one-dimension ask sends none at all. */
const MODE: Record<ResizeFit, number> = { contain: 2, cover: 3 };

/* Every box is asked for at 2x, so the image still holds up on a retina screen. */
const DENSITY = 2;

/**
 * Asks the media endpoint for the box an image is actually drawn in, instead of serving the stored
 * original. Dashboard uploads are capped at 20 MB / 8192 px, and the portal draws several of them
 * in boxes under 100 px — a poster once per show row.
 *
 * `width` and `height` are CSS pixels of the box; the 2x density is applied here so call sites read
 * as the SCSS they came from. Pass `fit` only when both dimensions are known.
 */
export const resizedMediaUrl = (
  media: Media | null | undefined,
  width?: number,
  height?: number,
  fit?: ResizeFit
): string => {
  const url = media?.url1x;

  if (!url) {
    return '';
  }

  // A vector is resolution-independent, and the resizer would only rasterise it.
  if (media?.is_extension_vector) {
    return url;
  }

  const params: string[] = [];

  if (width) {
    params.push(`w=${Math.round(width * DENSITY)}`);
  }

  if (height) {
    params.push(`h=${Math.round(height * DENSITY)}`);
  }

  if (width && height && fit) {
    params.push(`m=${MODE[fit]}`);
  }

  if (!params.length) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}${addWebp()}`;
};
